import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import { Factory, MapPin } from '@phosphor-icons/react';

import Footer from '../../components/Footer';
import { Link } from '../../components/Link';
import { BreadcrumbNavigation } from '../../components/BreadcrumbNavigation';
import { BestCompaniesCompanyRow } from './components/BestCompaniesCompanyRow';
import { BestCompaniesHubListSkeleton } from './components/BestCompaniesHubListSkeleton';
import { INDUSTRY_DIMENSIONS, CITY_DIMENSIONS } from './dimensionConfig';
import type { Data } from './+data.server';
import { getVikeSerializedPageData } from '../../utils/vike/get-vike-serialized-page-data';
import {
  BEST_COMPANIES_HUB_SCRIPT_ID,
  getBestCompaniesHubDataFromScript,
} from './parseBestCompaniesHubInitialScript';
import {
  resolveBestCompaniesHubData,
  richerBestCompaniesHubPayload,
} from '../../utils/vike/resolve-best-companies-ssr-data';
import { useBestCompaniesHubClientCompanies } from '../../utils/best-companies/use-best-companies-hub-client-companies';
import { getBestCompaniesDimensionDisplayLabel } from '../../utils/seo/best-companies-dimension-labels';
import { getBestCompaniesHubRankingYear, type BestCompaniesLocale } from '../../utils/seo/best-companies-meta';
import {
  bestCompaniesIndustryDimensionPath,
  bestCompaniesLocationDimensionPath,
} from '../../utils/routing/best-companies-paths';

/** Set to false when industry/city dimension browsing is ready to ship again. */
const BEST_COMPANIES_DIMENSION_BROWSING_DISABLED = true;

function DimensionCard({
  slug,
  label,
  type,
  disabled,
}: {
  slug: string;
  label: string;
  type: string;
  disabled?: boolean;
}) {
  const Icon = type === 'city' ? MapPin : Factory;
  const cardClassName = [
    'block p-4 bg-jc-surface rounded-2xl border border-jc-border shadow-md transition-all',
    disabled
      ? 'cursor-not-allowed opacity-90'
      : 'hover:border-jc-accent-soft hover:bg-jc-elevated/60 group',
  ].join(' ');

  const inner = (
    <div className="flex items-center gap-3">
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-jc-accent-muted text-jc-accent"
        aria-hidden
      >
        <Icon className="h-5 w-5" weight="duotone" />
      </span>
      <div className="min-w-0">
        <p
          className={
            disabled
              ? 'text-sm font-semibold text-jc-muted truncate'
              : 'text-sm font-semibold text-jc-ink group-hover:text-jc-accent transition-colors truncate'
          }
        >
          {label}
        </p>
        <p className="text-xs text-jc-muted mt-0.5">
          {disabled ? (
            <FormattedMessage
              id="bestCompanies.card.ctaDisabled"
              defaultMessage="Coming soon"
              description="Subline on best-companies dimension card when browsing is disabled"
            />
          ) : (
            <FormattedMessage
              id="bestCompanies.card.cta"
              defaultMessage="Best employers →"
              description="Subline on best-companies dimension card"
            />
          )}
        </p>
      </div>
    </div>
  );

  if (disabled) {
    return (
      <div className={cardClassName}>
        {inner}
      </div>
    );
  }

  const href =
    type === 'city' ? bestCompaniesLocationDimensionPath(slug) : bestCompaniesIndustryDimensionPath(slug);

  return (
    <Link href={href} className={cardClassName}>
      {inner}
    </Link>
  );
}

export default function BestCompaniesHubPage() {
  const intl = useIntl();
  const pageContext = usePageContext();
  const locale = (pageContext?.locale ?? 'en') as BestCompaniesLocale;
  const dataFromHook = useData<Data>();
  const dataFromContext = (pageContext as { data?: Data } | undefined)?.data;
  const routeKey =
    (pageContext as { urlPathname?: string; urlOriginal?: string } | undefined)?.urlPathname ||
    (typeof pageContext?.urlOriginal === 'string' ? pageContext.urlOriginal : '') ||
    (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '') ||
    '';
  const serializedForResolve =
    typeof document === 'undefined'
      ? undefined
      : richerBestCompaniesHubPayload(
          getVikeSerializedPageData<Data>(),
          getBestCompaniesHubDataFromScript(),
        );
  const hubData = resolveBestCompaniesHubData(dataFromHook, dataFromContext, serializedForResolve);
  const { companies, isLoadingHubCompanies } = useBestCompaniesHubClientCompanies(hubData.companies, routeKey);
  const rankingYear = getBestCompaniesHubRankingYear();

  return (
    <>
      <script
        id={BEST_COMPANIES_HUB_SCRIPT_ID}
        type="application/json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({ companies }) }}
      />
      <div className="min-h-screen bg-jc-page">
      <div className="max-w-4xl mx-auto px-3 pt-1.5 pb-12 sm:px-4 sm:pt-4 sm:pb-16">
        <BreadcrumbNavigation
          items={[
            {
              label: intl.formatMessage({
                id: 'bestCompanies.breadcrumb.current',
                defaultMessage: 'Best Employers',
                description: 'Breadcrumb current page — employer rankings hub',
              }),
            },
          ]}
          className="pb-1.5 text-jc-muted text-sm sm:pb-3 sm:text-base"
          linkClassName="text-jc-muted hover:text-jc-accent transition-colors duration-200"
          chevronClassName="h-4 w-4 mx-1 sm:mx-2 text-jc-contrast-subtle"
          useChevronRightIcon
        />

        <header className="mb-4 sm:mb-8">
          <h1 className="text-2xl font-bold text-jc-ink mb-1.5 sm:text-3xl sm:mb-3">
            <FormattedMessage
              id="bestCompanies.hub.title"
              values={{ year: rankingYear }}
              defaultMessage="Best companies to work for in Finland ({year})"
              description="H1 on best employers hub — year for freshness; Finland-targeted listings"
            />
          </h1>
          <div className="max-w-2xl space-y-3 text-sm leading-snug text-jc-muted sm:text-base sm:leading-relaxed">
            <p>
              <FormattedMessage
                id="bestCompanies.hub.lead"
                values={{ year: rankingYear }}
                defaultMessage="This page is our {year} snapshot of employers hiring through roles aimed at the Finnish job market. Many listings are remote or hybrid, so companies are not necessarily headquartered in Finland."
                description="Lead paragraph: year + scope of Finland-targeted listings"
              />
            </p>
            <p>
              <FormattedMessage
                id="bestCompanies.hub.methodologySummary"
                defaultMessage="Rankings use the JobCrawls Employer Score: a composite from live job-listing signals—salary transparency, remote and hybrid flexibility, hiring consistency over time, benefits depth, and how clearly requirements are stated. It is not based on employee reviews, surveys, or paid placements."
                description="On-page methodology summary for SEO and trust (hub links to full doc)"
              />
            </p>
            <p>
              <Link
                href="/about-employer-score"
                locale={locale}
                className="text-jc-accent font-medium hover:text-jc-cta-hover underline-offset-2 hover:underline"
              >
                <FormattedMessage
                  id="bestCompanies.hub.methodLink"
                  defaultMessage="Full methodology: how the Employer Score works"
                  description="Link to employer score methodology page"
                />
              </Link>
            </p>
            <p>
              <FormattedMessage
                id="bestCompanies.hub.scoreScale"
                defaultMessage="Scores are on a 0–100 scale. When many employers sit close together (for example in the high 30s to low 40s), the list is still ordered by those signals—#1 reflects the strongest combination in this cohort on the dimensions we measure, and lower ranks are often separated by small margins rather than a huge quality gap."
                description="Explains max score, tight spreads, and what rank order means"
              />
            </p>
          </div>
        </header>

        <section className="mb-6 sm:mb-10" aria-labelledby="best-companies-overall-heading">
          <h2
            id="best-companies-overall-heading"
            className="text-base font-semibold text-jc-ink mb-0.5 sm:text-lg sm:mb-2"
          >
            <FormattedMessage
              id="bestCompanies.hub.overallTitle"
              values={{ year: rankingYear }}
              defaultMessage="Top employers in {year} (all Finland-market listings)"
              description="Overall rankings heading with year"
            />
          </h2>
          <p className="text-xs text-jc-muted mb-2 max-w-2xl leading-snug sm:mb-4 sm:text-sm sm:leading-normal">
            <FormattedMessage
              id="bestCompanies.hub.overallSubtitle"
              values={{ year: rankingYear }}
              defaultMessage="Highest JobCrawls Employer Scores (0–100) among employers with active {year} listings for the Finnish job market—including local, hybrid, and remote roles."
              description="Subtitle: score scale, year, Finland-targeted listings"
            />
          </p>
          {isLoadingHubCompanies ? (
            <BestCompaniesHubListSkeleton />
          ) : companies.length === 0 ? (
            <p className="text-jc-muted text-sm">
              <FormattedMessage
                id={
                  BEST_COMPANIES_DIMENSION_BROWSING_DISABLED
                    ? 'bestCompanies.hub.overallEmptyNoDimensions'
                    : 'bestCompanies.hub.overallEmpty'
                }
                defaultMessage={
                  BEST_COMPANIES_DIMENSION_BROWSING_DISABLED
                    ? 'Rankings are not available right now. Try again later.'
                    : 'Rankings are not available right now. Try again later or browse by industry or city below.'
                }
                description="Shown when overall employer list failed to load or is empty"
              />
            </p>
          ) : (
            <ol className="space-y-2 max-w-3xl sm:space-y-3">
              {companies.map((company, i) => (
                <li key={company.objectID}>
                  <BestCompaniesCompanyRow company={company} rank={i + 1} />
                </li>
              ))}
            </ol>
          )}
        </section>

        {!BEST_COMPANIES_DIMENSION_BROWSING_DISABLED && (
          <>
            <section className="mb-10">
              <h2 className="text-lg font-semibold text-jc-ink mb-4">
                <FormattedMessage id="bestCompanies.hub.byIndustry" defaultMessage="By Industry" />
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {INDUSTRY_DIMENSIONS.map((d) => (
                  <DimensionCard
                    key={d.slug}
                    slug={d.slug}
                    label={getBestCompaniesDimensionDisplayLabel(d.slug, locale) ?? d.label}
                    type={d.type}
                  />
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-jc-ink mb-4">
                <FormattedMessage id="bestCompanies.hub.byCity" defaultMessage="By City" />
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {CITY_DIMENSIONS.map((d) => (
                  <DimensionCard
                    key={d.slug}
                    slug={d.slug}
                    label={getBestCompaniesDimensionDisplayLabel(d.slug, locale) ?? d.label}
                    type={d.type}
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
      <Footer />
    </div>
    </>
  );
}
